import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import { str } from "@agrigate/ts-utils/lib/zod";
import { Button, Icon, Image } from "@chakra-ui/react";
import { type ReactElement, useEffect, useState } from "react";
import MicIcon from "./assets/icons/mic-icon.svg?react";
import VoiceWavesIcon from "./assets/icons/voice-waves.gif";

declare global {
    interface Window {
        ReactNativeWebView?: {
            postMessage: (msg: string) => void;
        };
    }
}

export interface AudioCaptureTriggerProps {
    locale?: MaybeN<string>;
    onSuccess: (text: string) => void;
}

export default function AudioCaptureTrigger(
    p: AudioCaptureTriggerProps,
): ReactElement {
    const [isListening, setListening] = useState(false);

    useEffect(() => {
        if (!window.ReactNativeWebView) {
            console.error("Not running in webview: audio can not be captured");
            return;
        }
        if (!isListening) return;
        const receiveData = (event: MessageEvent<unknown>) => {
            // biome-ignore lint/suspicious/noExplicitAny: TODO Use zod here
            const data: any = event.data;
            if (data?.type === "speech_text_response") {
                setListening(false);
                p.onSuccess(str.parse(data.text));
            }
        };
        window.addEventListener("message", receiveData);
        return () => {
            window.removeEventListener("message", receiveData);
        };
    }, [isListening]);

    return (
        <Button
            size="xs"
            variant="ghost"
            onClick={() => {
                setListening(prev => {
                    const next = !prev;
                    window.ReactNativeWebView?.postMessage(
                        JSON.stringify({
                            type: next
                                ? "speech_start_listening"
                                : "speech_stop_listening",
                        }),
                    );
                    return next;
                });
            }}
        >
            <Icon>
                {isListening ? (
                    <Image src={VoiceWavesIcon} h="20px" w="20px" />
                ) : (
                    <MicIcon />
                )}
            </Icon>
        </Button>
    );
}
